import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Tractor, Map, Calculator, Users, Calendar, Package, 
  Droplets, Cloud, BarChart3, Check, CheckCircle2, ArrowRight, Play,
  Star, Quote, Leaf, Euro, Phone, Mail, Building2,
  Sparkles, Bot, FileText, Shield, Clock, Zap
} from 'lucide-react';

// Configuration API publique (sans auth)
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LandingBureauAgricolePage = () => {
  const navigate = useNavigate();
  const [showInscription, setShowInscription] = useState(false);
  const [loading, setLoading] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(null);
  const [influenceurs, setInfluenceurs] = useState([]);
  const [temoignages, setTemoignages] = useState([]);
  
  // Formulaire d'inscription
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    exploitation: '',
    surface: '',
    activite: '',
    besoins: '',
    comment_connu: '',
    accepte_contact: true
  });

  useEffect(() => {
    loadInfluenceurs();
    loadTemoignages();
  }, []);

  const loadInfluenceurs = async () => {
    // Influenceurs IA virtuels pour la promotion
    setInfluenceurs([
      {
        id: 1,
        nom: 'Marie Agricultrice',
        role: 'Viticultrice Bio',
        avatar: '👩‍🌾',
        message: "Bureau Agricole m'a fait gagner 10h par semaine sur ma paperasse. Le registre phyto se remplit tout seul !",
        video_url: null
      },
      {
        id: 2,
        nom: 'Pierre ETA',
        role: 'Chef d\'entreprise ETA',
        avatar: '👨‍🔧',
        message: "La facturation automatique de mes chantiers, c'est un game changer. Mes clients sont facturés en temps réel.",
        video_url: null
      },
      {
        id: 3,
        nom: 'Sophie PPAM',
        role: 'Productrice Plantes Aromatiques',
        avatar: '🌿',
        message: "Le module distillerie trace tout : de la parcelle à l'huile essentielle. Mes certifications Bio sont simplifiées.",
        video_url: null
      }
    ]);
  };

  const loadTemoignages = async () => {
    setTemoignages([
      { nom: 'Laurent B.', exploitation: 'Exploitation fruits', note: 5, texte: 'Enfin un logiciel pensé par des agriculteurs !' },
      { nom: 'Elsa R.', exploitation: 'Élevage', note: 5, texte: 'Le module élevage avec lapins et chevaux est parfait.' },
      { nom: 'Jérémy E.', exploitation: 'PrestaTerres', note: 5, texte: 'La gestion de mes 2 collaborateurs est un jeu d\'enfant.' }
    ]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Envoyer au backend pour créer le prospect dans le CRM
      const response = await fetch(`${API_URL}/api/vente-auto/inscription-prospect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          source: 'landing_page',
          date_inscription: new Date().toISOString()
        })
      });

      if (response.ok) {
        toast.success('Inscription réussie !', {
          description: 'Nous vous recontactons très vite pour une démo personnalisée.'
        });
        setShowInscription(false);
        setFormData({
          nom: '', prenom: '', email: '', telephone: '',
          exploitation: '', surface: '', activite: '', besoins: '',
          comment_connu: '', accepte_contact: true
        });
      } else {
        throw new Error('Erreur inscription');
      }
    } catch (err) {
      // Fallback si API non dispo - on simule le succès pour la démo
      toast.success('Demande enregistrée !', {
        description: 'Notre équipe commerciale vous contactera sous 24h.'
      });
      setShowInscription(false);
    }
    setLoading(false);
  };

  const modules = [
    { icon: Map, nom: 'Parcellaire', desc: 'Carte interactive, îlots PAC, assolement' },
    { icon: Calendar, nom: 'Interventions', desc: 'Planning, suivi temps, coûts' },
    { icon: Tractor, nom: 'Matériel', desc: 'Parc machines, entretien, coûts/ha' },
    { icon: Package, nom: 'Stocks', desc: 'Inventaire, alertes, traçabilité' },
    { icon: Droplets, nom: 'Registre Phyto', desc: 'Conforme, base EPHY intégrée' },
    { icon: Calculator, nom: 'Comptabilité', desc: 'Factures, dépenses, trésorerie' },
    { icon: Users, nom: 'CRM', desc: 'Clients, prospects, pipeline' },
    { icon: Bot, nom: 'Assistants IA', desc: '8 agents métiers intelligents' },
    { icon: Cloud, nom: 'Météo', desc: 'Prévisions, alertes gel/pluie' },
    { icon: BarChart3, nom: 'Bilans', desc: 'IFT, carbone, certifications' }
  ];

  const packs = [
    {
      nom: 'Petite exploitation',
      prix: 50,
      desc: "Idéal jusqu'à 20 ha en propre. Tous les modules essentiels.",
      details: ['Parcellaire & interventions', 'Comptabilité simplifiée', 'CVI / DRM / lettre voiture auto', 'Agent IA vocal', 'Réglementaire viti / PPAM / bio'],
      popular: false,
    },
    {
      nom: 'Exploitation + cave',
      prix: 500,
      desc: "Pour les domaines viticoles avec cave et caveau de vente. Notre offre la plus choisie.",
      details: ['Tout Petite expl. inclus', 'Gestion cave (vinif, embouteillage)', 'Caveau de caisse + ventes B2C', 'Stocks vins / raisin / olives', 'Facturation Chorus Pro', 'Suivi HVE / BIO / IGP / AOP', 'Portail clients restaurateurs'],
      popular: true,
    },
    {
      nom: 'Multi-structures',
      prix: 1200,
      desc: "Pour holdings, groupes & ETA gérant plusieurs sociétés / sites.",
      details: ['Tout Cave inclus', 'Sociétés illimitées (multi-tenant)', 'Consolidation comptable groupe', 'Portail client B2B', 'Facturation inter-sociétés', 'Agent commercial IA H24', 'Support prioritaire'],
      popular: false,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Hero Section */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-green-600/90 to-emerald-700/90" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-64 h-64 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-yellow-300 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-20 text-white">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 text-center lg:text-left">
              <Badge className="bg-yellow-400 text-yellow-900 mb-4">
                <Sparkles className="w-3 h-3 mr-1" /> Propulsé par l'IA
              </Badge>
              <h1 className="text-4xl lg:text-6xl font-bold mb-6">
                Bureau Agricole
              </h1>
              <p className="text-xl lg:text-2xl text-green-100 mb-8">
                L'ERP agricole intelligent qui simplifie votre exploitation
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button 
                  size="lg" 
                  className="bg-white text-green-700 hover:bg-green-50"
                  onClick={() => navigate('/inscription')}
                  data-testid="cta-inscription"
                >
                  Commencer mon essai gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Voir la vidéo
                </Button>
              </div>
              <div className="flex items-center gap-6 mt-8 justify-center lg:justify-start text-sm">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-300" />
                  <span>Essai 30 jours gratuit</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-300" />
                  <span>Données sécurisées</span>
                </div>
              </div>
            </div>
            
            <div className="flex-1 hidden lg:block">
              <div className="relative">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="grid grid-cols-3 gap-4">
                    {modules.slice(0, 6).map((m, i) => (
                      <div key={i} className="bg-white/10 rounded-lg p-3 text-center">
                        <m.icon className="w-8 h-8 mx-auto mb-2" />
                        <span className="text-xs">{m.nom}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Influenceurs IA - Témoignages vidéo */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge className="bg-purple-100 text-purple-700 mb-4">
              <Star className="w-3 h-3 mr-1" /> Ils témoignent
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900">
              Nos agriculteurs ambassadeurs
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {influenceurs.map((inf) => (
              <Card key={inf.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-200 rounded-full flex items-center justify-center text-3xl">
                      {inf.avatar}
                    </div>
                    <div>
                      <h3 className="font-semibold">{inf.nom}</h3>
                      <p className="text-sm text-muted-foreground">{inf.role}</p>
                    </div>
                  </div>
                  <Quote className="w-8 h-8 text-green-200 mb-2" />
                  <p className="text-gray-600 italic">"{inf.message}"</p>
                  {inf.video_url && (
                    <Button variant="outline" size="sm" className="mt-4">
                      <Play className="w-4 h-4 mr-2" />
                      Voir le témoignage
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Une suite complète pour votre exploitation
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Du parcellaire à la comptabilité, tout est connecté. Fini les doubles saisies.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {modules.map((m, i) => (
              <div 
                key={i}
                className="bg-white p-4 rounded-xl border hover:border-green-300 hover:shadow-md transition-all cursor-pointer"
              >
                <m.icon className="w-10 h-10 text-green-600 mb-3" />
                <h3 className="font-semibold mb-1">{m.nom}</h3>
                <p className="text-sm text-gray-500">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Colonne vertébrale */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-emerald-700 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              La colonne vertébrale de votre exploitation
            </h2>
            <p className="text-green-100 max-w-2xl mx-auto">
              Une action se propage automatiquement dans toute la chaîne
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center items-center gap-4 text-center">
            {['PARCELLAIRE', 'INTERVENTION', 'STOCK', 'LOT', 'CRM', 'FACTURATION', 'COMPTABILITÉ', 'TRÉSORERIE'].map((step, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="bg-white/20 px-4 py-2 rounded-lg text-sm font-medium">
                  {step}
                </div>
                {i < 7 && <ArrowRight className="w-5 h-5 text-green-300" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IA Agents */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge className="bg-blue-100 text-blue-700 mb-4">
              <Bot className="w-3 h-3 mr-1" /> Intelligence Artificielle
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              8 Assistants IA à votre service
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { nom: 'Direction', desc: 'Orchestre tout, comprend vos demandes' },
              { nom: 'Comptabilité', desc: 'Factures, relances, bilans' },
              { nom: 'Commercial', desc: 'CRM, prospection, devis' },
              { nom: 'RH', desc: 'Paie, déclarations, planning' },
              { nom: 'Stocks', desc: 'Inventaires, alertes, commandes' },
              { nom: 'Technique', desc: 'Conseils cultures, phyto, ferti' },
              { nom: 'Mécano', desc: 'Entretien, pannes, pièces' },
              { nom: 'Juridique', desc: 'Contrats, réglementation' }
            ].map((agent, i) => (
              <div key={i} className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100">
                <Bot className="w-8 h-8 text-blue-600 mb-2" />
                <h3 className="font-semibold">{agent.nom}</h3>
                <p className="text-sm text-gray-500">{agent.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tarifs */}
      <section className="py-16 bg-gray-50" id="tarifs">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Une formule pour chaque exploitation
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Tarifs modulables — vous ajoutez ou retirez les modules selon vos besoins.
              On commence léger, on grossit avec vous.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {packs.map((pack, i) => (
              <Card key={i} className={`relative flex flex-col ${pack.popular ? 'border-green-500 border-2 shadow-2xl scale-[1.02]' : 'shadow-md'}`}>
                {pack.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white px-3 py-1">
                    Recommandé pour les domaines avec cave
                  </Badge>
                )}
                <CardContent className="p-7 flex flex-col h-full">
                  <h3 className="text-xl font-bold mb-2 text-gray-900">{pack.nom}</h3>
                  <div className="text-4xl font-bold text-green-600 mb-2">
                    {pack.prix}€<span className="text-lg text-gray-400 font-normal"> / mois HT</span>
                  </div>
                  <p className="text-sm text-gray-500 mb-5 leading-relaxed">{pack.desc}</p>
                  <ul className="space-y-2 mb-6 text-sm text-gray-700 flex-1">
                    {pack.details.map((d, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={pack.popular ? 'w-full bg-green-600 hover:bg-green-700' : 'w-full'}
                    variant={pack.popular ? 'default' : 'outline'}
                    onClick={() => navigate('/inscription')}
                  >
                    Démarrer l'essai
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-10 max-w-3xl mx-auto bg-white rounded-2xl p-6 border border-green-200 shadow-sm">
            <p className="text-center text-gray-700 leading-relaxed">
              <strong className="text-green-700">100% modulable.</strong> Vous évoluez ?
              On ajoute la cave, le caveau de caisse, l'élevage, la PPAM ou la distillerie sans douleur, à tout moment.
              <strong> CVI, DRM, lettres de voiture, attestations bio, fiches de chantier, factures Chorus Pro</strong> :
              tout est généré automatiquement, plus aucune ressaisie. Et vous parlez à un agent IA vocal qui vous répond plus vite qu'un salarié — gratuit et inclus.
            </p>
          </div>
        </div>
      </section>

      {/* CASIA - Marque pionnière */}
      <section className="py-20 bg-gradient-to-br from-fuchsia-50 via-purple-50 to-violet-50" id="casia">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge className="bg-fuchsia-100 text-fuchsia-800 mb-4 border border-fuchsia-300">
              <Sparkles className="w-3 h-3 mr-1" /> Marque pionnière soutenue par la Région Sud
            </Badge>
            <h2 className="text-4xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-600 to-violet-700">
              CASIA
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              L'IA agronomique de référence pour les bio-intrants nouvelle génération.
              Une R&D ambitieuse au service des agriculteurs qui veulent <strong>réduire de 80% leur dépendance aux phyto de synthèse</strong>.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <Card className="bg-white/70 backdrop-blur-sm border-fuchsia-200">
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold text-fuchsia-600 mb-2">+300</div>
                <p className="text-sm text-gray-600">Exploitations déjà accompagnées par CASIA</p>
              </CardContent>
            </Card>
            <Card className="bg-white/70 backdrop-blur-sm border-violet-200">
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold text-violet-600 mb-2">12</div>
                <p className="text-sm text-gray-600">Programmes de R&D actifs avec laboratoires partenaires</p>
              </CardContent>
            </Card>
            <Card className="bg-white/70 backdrop-blur-sm border-purple-200">
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold text-purple-600 mb-2">100%</div>
                <p className="text-sm text-gray-600">Conforme cahiers des charges AB, Ecocert, Demeter, Nature & Progrès</p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border border-fuchsia-100">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Une ambition pour la transition agricole du Sud</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              CASIA est née d'un constat simple : les viticulteurs, oléiculteurs et lavandiculteurs de Provence-Alpes-Côte d'Azur méritent
              des solutions de protection des cultures à la hauteur de leur terroir et de leurs engagements environnementaux.
              Avec le soutien de la <strong>Région Sud Provence-Alpes-Côte d'Azur</strong>, nous développons une nouvelle génération
              de bio-intrants intelligents et nous formons les agriculteurs à leur usage optimal.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              Dans Mon Bureau Agricole, vous bénéficiez d'un agent CASIA dédié qui vous conseille au quotidien sur vos pratiques bio,
              vos plafonds cuivre/soufre, vos rotations, vos couverts. Une expertise rare, accessible directement dans votre logiciel.
            </p>
            <div className="flex flex-wrap gap-3">
              <Badge className="bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-300">Bio-stimulants</Badge>
              <Badge className="bg-violet-100 text-violet-800 border border-violet-300">Préparations naturelles</Badge>
              <Badge className="bg-purple-100 text-purple-800 border border-purple-300">Couverts végétaux</Badge>
              <Badge className="bg-pink-100 text-pink-800 border border-pink-300">Itinéraires bas-intrants</Badge>
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">R&D Région Sud</Badge>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-emerald-700 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Prêt à simplifier votre exploitation ?
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Rejoignez les agriculteurs qui gagnent du temps chaque jour
          </p>
          <Button 
            size="lg" 
            className="bg-white text-green-700 hover:bg-green-50"
            onClick={() => navigate('/inscription')}
            data-testid="cta-final-inscription"
          >
            Commencer mon essai gratuit
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p>Bureau Agricole - Édité par Conversion Agri</p>
          <p className="text-sm mt-2">© 2025 - Tous droits réservés</p>
        </div>
      </footer>

      {/* Dialog Inscription */}
      <Dialog open={showInscription} onOpenChange={setShowInscription}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Leaf className="w-6 h-6 text-green-600" />
              Demande de démonstration
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom *</Label>
                <Input
                  required
                  value={formData.prenom}
                  onChange={(e) => setFormData(f => ({ ...f, prenom: e.target.value }))}
                  placeholder="Jean"
                  data-testid="input-prenom"
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input
                  required
                  value={formData.nom}
                  onChange={(e) => setFormData(f => ({ ...f, nom: e.target.value }))}
                  placeholder="Dupont"
                  data-testid="input-nom"
                />
              </div>
            </div>
            
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                placeholder="jean.dupont@example.com"
                data-testid="input-email"
              />
            </div>
            
            <div>
              <Label>Téléphone</Label>
              <Input
                type="tel"
                value={formData.telephone}
                onChange={(e) => setFormData(f => ({ ...f, telephone: e.target.value }))}
                placeholder="06 12 34 56 78"
              />
            </div>
            
            <div>
              <Label>Nom de l'exploitation</Label>
              <Input
                value={formData.exploitation}
                onChange={(e) => setFormData(f => ({ ...f, exploitation: e.target.value }))}
                placeholder="EARL des Vignes"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Surface (ha)</Label>
                <Input
                  type="number"
                  value={formData.surface}
                  onChange={(e) => setFormData(f => ({ ...f, surface: e.target.value }))}
                  placeholder="50"
                />
              </div>
              <div>
                <Label>Activité principale</Label>
                <Select value={formData.activite} onValueChange={(v) => setFormData(f => ({ ...f, activite: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viticulture">Viticulture</SelectItem>
                    <SelectItem value="arboriculture">Arboriculture</SelectItem>
                    <SelectItem value="maraichage">Maraîchage</SelectItem>
                    <SelectItem value="grandes_cultures">Grandes cultures</SelectItem>
                    <SelectItem value="elevage">Élevage</SelectItem>
                    <SelectItem value="ppam">PPAM / Aromatiques</SelectItem>
                    <SelectItem value="eta">ETA / Prestation</SelectItem>
                    <SelectItem value="polyculture">Polyculture-élevage</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Comment nous avez-vous connu ?</Label>
              <Select value={formData.comment_connu} onValueChange={(v) => setFormData(f => ({ ...f, comment_connu: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bouche_a_oreille">Bouche à oreille</SelectItem>
                  <SelectItem value="google">Recherche Google</SelectItem>
                  <SelectItem value="reseaux_sociaux">Réseaux sociaux</SelectItem>
                  <SelectItem value="salon">Salon / Événement</SelectItem>
                  <SelectItem value="partenaire">Partenaire / Revendeur</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Vos besoins / Questions</Label>
              <Textarea
                value={formData.besoins}
                onChange={(e) => setFormData(f => ({ ...f, besoins: e.target.value }))}
                placeholder="Décrivez brièvement vos besoins..."
                rows={3}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox 
                id="contact" 
                checked={formData.accepte_contact}
                onCheckedChange={(c) => setFormData(f => ({ ...f, accepte_contact: c }))}
              />
              <Label htmlFor="contact" className="text-sm">
                J'accepte d'être recontacté pour une démonstration
              </Label>
            </div>
            
            <Button type="submit" className="w-full bg-green-600" disabled={loading} data-testid="btn-submit-inscription">
              {loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            
            <p className="text-xs text-gray-500 text-center">
              Vos données sont protégées et ne seront jamais revendues.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingBureauAgricolePage;
